import { RefCallback } from 'react';
import {
    LuCalendarRange,
    LuChartNoAxesCombined,
    LuGitMerge,
    LuNetwork,
    LuPercent,
    LuPin,
    LuReceipt,
    LuRotateCcw,
    LuSlidersHorizontal,
    LuSprout,
} from 'react-icons/lu';

import { cn } from 'utils/cn';

const featureGroups = [
    {
        name: 'Automate',
        features: [
            {
                name: 'Automatic Rebalancing',
                description: 'Automatically rebalance portfolios to target allocations on a scheduled basis.',
                icon: LuRotateCcw,
                color: 'bg-[#F3EAF8] text-[#8A5CB0]',
            },
            {
                name: 'Automated Billing',
                description: 'Auto-raise cash for advisory fees each billing cycle.',
                icon: LuReceipt,
                color: 'bg-[#FBEEE3] text-[#A0673A]',
            },
        ],
    },
    {
        name: 'Optimize',
        features: [
            {
                name: 'Withdrawal Plan',
                description: 'Auto-raise cash for scheduled withdrawals related to a client’s plan.',
                icon: LuCalendarRange,
                color: 'bg-[#FBEEE3] text-[#A0673A]',
            },
            {
                name: 'CashEQ Sweep',
                description:
                    'Idle available cash is swept into higher-yield equivalents, keeping uninvested cash to a minimum.',
                icon: LuSprout,
                color: 'bg-[#EAF6E6] text-[#3E8A4A]',
            },
            {
                name: 'Tax Budget',
                description: 'Limit realized capital gains and prioritize tax benefits while rebalancing.',
                icon: LuPercent,
                color: 'bg-[#E3F5EC] text-[#2E8A5C]',
            },
            {
                name: 'Tax Loss Harvesting',
                description:
                    'Harvest large losses automatically or on demand, and replace harvested assets with similar alternatives.',
                icon: LuChartNoAxesCombined,
                color: 'bg-[#E6EEF9] text-[#4A6FA5]',
            },
        ],
    },
    {
        name: 'Customize',
        features: [
            {
                name: 'Personalized Direct Indexing',
                description:
                    'Hold U.S. equity as direct stocks instead of an index ETF, so screens, factor tilts, etc. can be applied and customized to your client’s preferences.',
                icon: LuSlidersHorizontal,
                color: 'bg-[#F3EAF8] text-[#8A5CB0]',
            },
            {
                name: 'Coordinated Account Group',
                description: 'Manage multiple accounts under one, shared investment policy.',
                icon: LuGitMerge,
                color: 'bg-[#E3F3F4] text-[#2F7F86]',
            },
            {
                name: 'Asset Location Policy',
                description: 'Hold each asset class in the account where it is taxed least across a coordinated group.',
                icon: LuNetwork,
                color: 'bg-[#E3F3F4] text-[#2F7F86]',
            },
            {
                name: 'Exclusions and Custom Targets',
                description: 'Carve individual holdings out of the model — never sold, never bought, or never traded.',
                icon: LuPin,
                color: 'bg-[#FBE6E3] text-[#B23A2E]',
            },
        ],
    },
];

export const policySectionNames = ['Boost', ...featureGroups.map((group) => group.name)];

interface PolicyFeatureListProps {
    sectionRef: (name: string) => RefCallback<HTMLElement>;
}

export const PolicyFeatureList = ({ sectionRef }: PolicyFeatureListProps) => (
    <>
        <h2 className="text-cocoa-900 mb-5 text-2xl font-medium">What’s included</h2>
        <div className="border-cocoa-100 overflow-hidden rounded-xl border">
            {featureGroups.map((group) => (
                <section key={group.name} aria-label={group.name} className="scroll-mt-4" ref={sectionRef(group.name)}>
                    <h3 className="border-cocoa-100 bg-cocoa-0 text-cocoa-600 border-b px-6 pt-5 pb-3 text-xs font-medium tracking-widest uppercase">
                        {group.name}
                    </h3>
                    {group.features.map(({ name, description, icon: Icon, color }) => (
                        <div
                            key={name}
                            className="border-cocoa-100 flex min-h-19 items-center gap-5 border-b px-6 py-4 last:border-b-0"
                        >
                            <div
                                className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', color)}
                            >
                                <Icon aria-hidden="true" className="h-5 w-5" />
                            </div>
                            <div className="min-w-0">
                                <h4 className="text-cocoa-900 text-sm font-medium">{name}</h4>
                                <p className="text-cocoa-600 mt-1 text-sm leading-relaxed">{description}</p>
                            </div>
                        </div>
                    ))}
                </section>
            ))}
        </div>
    </>
);
